// src/components/LoginForm.js
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Grid,
  InputAdornment,
  MobileStepper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import { 
  Mail, 
  LogIn, 
  Info,
  Zap,
  UserPlus,
  Building2,
  User,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  Phone
} from 'lucide-react';
import { 
  WhatsApp 
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { keyframes } from '@mui/system';
import { AuthContext } from '../../contexts/auth/AuthContext';
import { KoorAILandingPage } from '../marketing';

const API_URL = process.env.REACT_APP_API_URL;

// Define a fade animation for carousel
const fadeAnimation = keyframes`
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;
// OTP Login Page Component
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, currentUser } = useContext(AuthContext);
  
  // State for email input and OTP flow with WhatsApp support
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [remainingTime, setRemainingTime] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timeoutAlert, setTimeoutAlert] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [whatsappAvailable, setWhatsappAvailable] = useState(true); // Start with true for now
  // Carousel state
  const [activeStep, setActiveStep] = useState(0);
  const carouselImages = [
    `${process.env.PUBLIC_URL}/images/image1.svg`,
    `${process.env.PUBLIC_URL}/images/image2.png`,
    `${process.env.PUBLIC_URL}/images/image3.png`,
    `${process.env.PUBLIC_URL}/images/image4.svg`
  ];
  const maxSteps = carouselImages.length;

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
    }, 5000); // Change image every 5 seconds

    return () => {
      clearInterval(timer);
    };
  }, [maxSteps]);

  // OTP Timer effect - Fixed implementation
  useEffect(() => {
    let timer;
    
    if (remainingTime > 0 && otpSent) {
      timer = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [remainingTime, otpSent]);

  // Trial Signup State
  const [showTrialSignup, setShowTrialSignup] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [trialFormData, setTrialFormData] = useState({
    name: '',
    userType: 'individual',
    companyName: ''
  });

  // Check for any passed messages
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      
      // Pre-fill email if passed
      if (location.state.email) {
        setEmail(location.state.email);
        setOtpSent(true);
      }
    }
  }, [location.state]);
  
  // Check if already logged in and redirect based on role
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      const { from } = location.state || {};
      if (from) {
        navigate(from, { replace: true });
        return;
      }
      
      // Redirect based on role
      if (['admin', 'superadmin'].includes(currentUser.role)) {
        navigate('/profiles/admin', { replace: true });
      } else {
        navigate('/profiles/profile', { replace: true });
      }
    }
  }, [isAuthenticated, currentUser, navigate, location]);

  // Check if user was redirected due to session timeout
  useEffect(() => {
    if (location.state?.timeout) {
      setTimeoutAlert(true);
    }
  }, [location.state]);  
  
  // Email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Phone number validation
  const validatePhone = (phone) => {
    const phoneRegex = /^\+\d{1,15}$/;
    return phoneRegex.test(phone);
  };

  // Function to handle OTP sending unified
  const sendVerificationOTP = async (emailToUse) => {
    if (!validateEmail(emailToUse)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Try the API endpoint with /api prefix first
      let response;
      try {
        response = await axios.post(`${API_URL}/api/auth/send-otp`, { email: emailToUse });
      } catch (err) {
        // If that fails, try without the /api prefix
        if (err.response?.status === 429) {
          throw err; // Rethrow rate limit errors to handle them properly
        }
        response = await axios.post(`${API_URL}/auth/send-otp`, { email: emailToUse });
      }
      
      // Set the remaining time from the response
      const expiresIn = response.data.expiresIn || 900; // Default 15 minutes
      setRemainingTime(expiresIn);
      
      // Disable resend button for 60 seconds
      setResendDisabled(true);
      setTimeout(() => setResendDisabled(false), 60000);
      
      setMessage('Verification code sent to your email');
      return true;
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.response?.data?.message || 'Failed to send verification code');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };
  

  // const handleSendOtp = async (e) => {
  //   if (e) e.preventDefault();
    
  //   setLoading(true);
  //   setMessage('Sending verification code...');
    
  //   // Set a temporary remaining time to prevent the "expired" message
  //   setRemainingTime(60); // Set temporary 60 seconds to prevent "expired" message
    
  //   // Show OTP input field immediately
  //   setOtpSent(true);
    
  //   // Start progress indicators
  //   let messageTimer = setTimeout(() => {
  //     setMessage('Preparing verification code...');
  //   }, 2500);
    
  //   let messageTimer2 = setTimeout(() => {
  //     setMessage('Almost there! Sending to your email...');
  //   }, 6000);
    
  //   try {
  //     const response = await axios.post(`${API_URL}/api/auth/send-otp`, { email });
      
  //     // Clear progress timers
  //     clearTimeout(messageTimer);
  //     clearTimeout(messageTimer2);
      
  //     if (response.data && response.data.expiresIn) {
  //       setRemainingTime(response.data.expiresIn);
  //       setMessage('Verification code sent to your email');
  //     }
  //   } catch (error) {
  //     clearTimeout(messageTimer);
  //     clearTimeout(messageTimer2);
      
  //     setError(error.response?.data?.message || 'Failed to send verification code');
  //     setOtpSent(false); // Hide OTP input on error
  //     setRemainingTime(0); // Reset remaining time on error
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  // Handle OTP verification
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    
    // Validate input based on delivery method
    if (deliveryMethod === 'email') {
      if (!email || !validateEmail(email)) {
        setError('Please enter a valid email address');
        return;
      }
    } else if (deliveryMethod === 'whatsapp') {
      if (!phoneNumber || !validatePhone(phoneNumber)) {
        setError('Please enter a valid phone number with country code (e.g., +1234567890)');
        return;
      }
    }

    // Determine if we're in signup mode based on the showTrialSignup state
    const isLoginMode = !showTrialSignup;
    const identifier = deliveryMethod === 'email' ? email : phoneNumber;
    
    // Clear any existing messages
    setMessage('');
    setError('');
    
    setLoading(true);
    const deliveryText = deliveryMethod === 'email' ? 'email' : 'WhatsApp';
    setMessage(`Sending verification code via ${deliveryText}...`);
    
    // Set a temporary remaining time to prevent the "expired" message
    setRemainingTime(60); // Set temporary 60 seconds to prevent "expired" message
    
    // Declare timer variables at this scope so they're available in the catch block
    let messageTimer;
    let messageTimer2;
    
    try {
      // For login mode, check if user exists first (only for email for now)
      if (isLoginMode && deliveryMethod === 'email') {
        try {
          // Quick check if the email exists in the system
          const checkResponse = await axios.post(`${API_URL}/api/auth/check-email`, { email });
          
          // If email doesn't exist for login, show error
          if (!checkResponse.data.exists) {
            // Clear the sending message when showing error
            setMessage('');
            setError('This email is not registered. Please use the trial signup option below.');
            setLoading(false);
            return;
          }
          
          // Email exists, continue with OTP
          setMessage(`Verification code being sent via ${deliveryText}...`);
        } catch (error) {
          // Handle API errors during email check
          if (error.response?.status === 404) {
            // Clear the sending message when showing error
            setMessage('');
            setError('This email is not registered. Please use the trial signup option below.');
            setLoading(false);
            return;
          }
          // For other errors, continue with the normal flow
        }
      }
      
      // Show OTP input field immediately
      setOtpSent(true);
      
      // Start progress indicators
      messageTimer = setTimeout(() => {
        setMessage('Preparing verification code...');
      }, 2500);
      
      messageTimer2 = setTimeout(() => {
        setMessage(`Almost there! Sending to your ${deliveryText}...`);
      }, 6000);
      
      // Send OTP request with delivery method
      const requestData = {
        deliveryMethod,
        purpose: isLoginMode ? 'login' : 'signup'
      };

      if (deliveryMethod === 'email') {
        requestData.email = email;
      } else {
        requestData.mobile = phoneNumber;
        requestData.phoneNumber = phoneNumber;
      }

      const response = await axios.post(`${API_URL}/api/auth/send-otp`, requestData);
      
      // Clear progress timers
      clearTimeout(messageTimer);
      clearTimeout(messageTimer2);
      
      if (response.data && (response.data.expiresIn || response.data.success)) {
        const expiry = response.data.expiresIn || 300; // Default 5 minutes
        setRemainingTime(expiry);
        setMessage(`Verification code sent via ${deliveryText}`);
        setError(''); // Clear any errors
      }
    } catch (error) {
      // Clear any success message when showing error
      setMessage('');
      
      // Clear timers if they were set
      if (messageTimer) clearTimeout(messageTimer);
      if (messageTimer2) clearTimeout(messageTimer2);
      
      setError(error.response?.data?.message || `Failed to send verification code via ${deliveryText}`);
      setOtpSent(false); // Hide OTP input on error
      setRemainingTime(0); // Reset remaining time on error
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6 || !/^\d+$/.test(otp)) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Try the API endpoint with /api prefix first, supporting both email and WhatsApp
      let response;
      const requestData = {
        otp,
        deliveryMethod
      };

      if (deliveryMethod === 'email') {
        requestData.email = email;
      } else {
        requestData.mobile = phoneNumber;
        requestData.phoneNumber = phoneNumber;
      }

      try {
        response = await axios.post(`${API_URL}/api/auth/verify-otp`, requestData);
      } catch (err) {
        // If that fails, try without the /api prefix
        response = await axios.post(`${API_URL}/auth/verify-otp`, requestData);
      }
      
      // Handle both old format (data.token, data.user) and new format (data.data.token, data.data.user)
      const responseData = response.data.data || response.data;
      const { token, user } = responseData;
      
      // Create a new user object to avoid reference issues
      const userData = { ...user };
      
      // Check if we need to query for profile info to get role
      if (!userData.role) {
        try {
          // Try to get user profile info which might include role and trial status
          const profileResponse = await axios.get(
            `${API_URL}/profiles/${userData.id || userData._id}`,
            { headers: { Authorization: `Bearer ${token}` } } // Add token for authorization
          );
          
          // If we got a response, update user with profile info
          if (profileResponse.data) {
            // Check if the role is in the response data directly or in a user property
            const profileData = profileResponse.data.user || profileResponse.data;
            userData.role = profileData.role || 'user';
            
            // Store profile ID for direct access
            userData.profileId = profileData.id;
            
            // Copy trial information if available
            if (profileData.is_trial) {
              userData.is_trial = profileData.is_trial;
              userData.trial_start_date = profileData.trial_start_date;
              userData.trial_end_date = profileData.trial_end_date;
            }
            
            // Copy other relevant fields if available
            if (profileData.name) userData.name = profileData.name;
          }
        } catch (profileErr) {
          console.error('Could not fetch profile to determine role:', profileErr);
          // Default to user role if we can't determine
          userData.role = 'user';
        }
      }
      
      // Use the login function from AuthContext to set user state and token
      const loggedInUser = await login(token, userData);
      
      // Redirect based on user role
      if (['admin', 'superadmin'].includes(loggedInUser.role)) {
        navigate('/profiles/admin', { replace: true });
      } else {
        navigate('/profiles/profile', { replace: true });
      }
    } catch (err) {
      if (err.response) {
        switch (err.response.status) {
          case 401:
            setError('Invalid or expired verification code');
            break;
          case 429:
            setError('Too many attempts. Please try again later.');
            break;
          default:
            setError(err.response?.data?.message || 'Verification failed');
        }
      } else {
        setError('Verification failed. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Handle trial form changes
  const handleTrialFormChange = (e) => {
    const { name, value } = e.target;
    setTrialFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle trial signup submission
  const handleTrialSignup = async () => {
    // Validate form based on delivery method
    if (deliveryMethod === 'email') {
      if (!email || !validateEmail(email)) {
        setError('Please enter a valid email address');
        return;
      }
    } else if (deliveryMethod === 'whatsapp') {
      if (!phoneNumber || !validatePhone(phoneNumber)) {
        setError('Please enter a valid phone number with country code (e.g., +1234567890)');
        return;
      }
    }
  
    if (!trialFormData.name.trim()) {
      setError('Please enter your name');
      return;
    }
  
    if (trialFormData.userType === 'company' && !trialFormData.companyName.trim()) {
      setError('Please enter your company name');
      return;
    }
  
    setLoading(true);
    setError('');
  
    try {
      // Prepare payload with delivery method support
      const payload = {
        name: trialFormData.name,
        userType: trialFormData.userType,
        companyName: trialFormData.companyName,
        deliveryMethod
      };

      if (deliveryMethod === 'email') {
        payload.email = email;
      } else {
        payload.mobile = phoneNumber;
        payload.phoneNumber = phoneNumber;
      }
  
      // Submit to backend
      const response = await axios.post(`${API_URL}/api/auth/create-trial`, payload);
  
      // Move to OTP verification step
      setShowTrialSignup(false);
      setOtpSent(true);
      setRemainingTime(response.data.expiresIn || 900);
      setMessage('Your trial account has been created! Please verify your email to continue.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create trial account');
    } finally {
      setLoading(false);
    }
  };

  // If showing landing page, render that instead
  if (showLandingPage) {
    return (
      <KoorAILandingPage 
        onLoginClick={() => setShowLandingPage(false)}
      />
    );
  }
  
  return (
    <Box 
      sx={{ 
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fa',
        p: 3
      }}
    >
      {timeoutAlert && (
        <Alert 
          severity="warning" 
          icon={<AlertTriangle />}
          sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16,
            maxWidth: 400,
            boxShadow: 3,
            borderRadius: 2
          }}
          onClose={() => setTimeoutAlert(false)}
        >
          Your previous session has expired due to inactivity. Please log in again.
        </Alert>
      )}
      
      {message && (
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16,
            maxWidth: 400,
            boxShadow: 3,
            borderRadius: 2
          }}
          onClose={() => setMessage('')}
        >
          {message}
        </Alert>
      )}
      
      <Grid container spacing={2} maxWidth="lg">
        <Grid item xs={12} md={6}>
          <Box 
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: { xs: 2, md: 8 }
            }}
          >
            <Typography 
              variant="h3" 
              component="h3" 
              fontWeight="bold" 
              sx={{ mb: 2 }}
            >
              Profile Manager
            </Typography>
            <Typography 
              variant="h5" 
              color="textSecondary" 
              sx={{ mb: 4 }}
            >
              Manage professional profiles, generate QR codes, and create email signatures
            </Typography>
            
            {/* Carousel - Only shown on medium screens and larger */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}>
              <Paper
                square
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 0,
                  bgcolor: 'transparent',
                  overflow: 'hidden',
                  borderRadius: 3,
                }}
              >
                {carouselImages.map((img, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={img}
                    alt={`Slide ${index + 1}`}
                    sx={{
                      display: index === activeStep ? 'block' : 'none',
                      width: '100%',
                      height: 'auto',
                      borderRadius: 3,
                      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                      animation: index === activeStep ? `${fadeAnimation} 0.5s ease-in-out` : 'none',
                    }}
                  />
                ))}
              </Paper>

              <MobileStepper
                steps={maxSteps}
                position="static"
                activeStep={activeStep}
                sx={{
                  bgcolor: 'transparent',
                  '& .MuiMobileStepper-dot': {
                    backgroundColor: 'rgba(37, 99, 235, 0.3)',
                  },
                  '& .MuiMobileStepper-dotActive': {
                    backgroundColor: 'rgba(37, 99, 235, 1)',
                  }
                }}
                nextButton={
                  <IconButton
                    size="small"
                    onClick={() => setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': {
                        bgcolor: 'white',
                      },
                      zIndex: 2,
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                }
                backButton={
                  <IconButton
                    size="small"
                    onClick={() => setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps)}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': {
                        bgcolor: 'white',
                      },
                      zIndex: 2,
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                }
              />
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card 
            elevation={6} 
            sx={{ 
              borderRadius: 3, 
              overflow: 'hidden',
              height: '100%',
              minHeight: { xs: 'auto', md: '600px' }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Box 
                sx={{ 
                  bgcolor: '#1a365d',
                  color: 'white',
                  p: 3,
                  background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {otpSent ? 'Enter Verification Code' : (showTrialSignup ? 'Start Free Trial' : 'Sign In')}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {otpSent 
                      ? 'Enter the code sent to your email' 
                      : (showTrialSignup 
                          ? 'Create your trial account' 
                          : 'Secure login with email verification')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowLandingPage(true)}
                    sx={{ 
                      mt: 1, 
                      color: 'white', 
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    🚀 Know More about KoorAI Products
                  </Button>
                </Box>
                <Box>
                  <img 
                    src="/logo.png" 
                    alt="Company Logo" 
                    style={{ 
                      height: '50px', 
                      width: 'auto',
                      marginLeft: '20px' 
                    }} 
                  />
                </Box>
              </Box>
              
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 4 }}>
                {message && (
                  <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage('')}>
                    {message}
                  </Alert>
                )}
                
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}
                
                {otpSent ? (
                  <form onSubmit={handleVerifyOtp}>
                    {remainingTime > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Alert 
                          icon={<Clock size={20} />} 
                          severity="info"
                          sx={{ width: '100%' }}
                        >
                          Code expires in: {formatTime(remainingTime)}
                        </Alert>
                      </Box>
                    )}
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      A verification code has been sent to <strong>{email}</strong>
                    </Typography>
                    
                    <TextField
                      label="Verification Code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      fullWidth
                      margin="normal"
                      required
                      inputProps={{ 
                        maxLength: 6,
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                      placeholder="Enter 6-digit code"
                      disabled={loading || remainingTime === 0}
                      autoComplete="one-time-code"
                    />
  
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={loading || remainingTime === 0}
                      startIcon={loading ? <CircularProgress size={20} /> : <LogIn />}
                      sx={{ py: 1.5, mt: 3 }}
                    >
                      {loading ? 'Verifying...' : 'Verify & Sign In'}
                    </Button>
                    
                    {remainingTime === 0 ? (
                      <Alert severity="warning" sx={{ mt: 3 }}>
                        <Typography variant="body2">
                          The verification code has expired.
                        </Typography>
                        <Button 
                          onClick={handleSendOtp}
                          disabled={loading || resendDisabled}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          Send New Code
                        </Button>
                      </Alert>
                    ) : (
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                          variant="text"
                          size="small"
                          disabled={loading || resendDisabled}
                          onClick={handleSendOtp}
                        >
                          {resendDisabled ? "Resend available shortly" : "Didn't receive the code? Resend"}
                        </Button>
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                          setRemainingTime(0); // Reset timer when going back
                        }}
                        disabled={loading}
                      >
                        Use a different email
                      </Button>
                    </Box>
                  </form>
                ) : showTrialSignup ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleTrialSignup(); }}>
                    {/* Delivery Method Toggle for Signup */}
                    {whatsappAvailable && (
                      <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Choose delivery method
                        </Typography>
                        <ToggleButtonGroup
                          value={deliveryMethod}
                          exclusive
                          onChange={(e, newMethod) => {
                            if (newMethod !== null) {
                              setDeliveryMethod(newMethod);
                              setError('');
                              setMessage('');
                            }
                          }}
                          sx={{
                            '& .MuiToggleButton-root': {
                              border: '1px solid',
                              borderColor: '#e0e0e0',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              minWidth: '120px',
                              fontWeight: 500,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                borderColor: '#2563eb',
                                backgroundColor: 'rgba(37, 99, 235, 0.04)'
                              },
                              '&.Mui-selected': {
                                background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
                                color: 'white',
                                borderColor: '#2563eb',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
                                  opacity: 0.95
                                }
                              }
                            }
                          }}
                        >
                          <ToggleButton value="email" aria-label="email delivery">
                            <Mail size={18} style={{ marginRight: '8px' }} />
                            Email
                          </ToggleButton>
                          <ToggleButton value="whatsapp" aria-label="whatsapp delivery">
                            <WhatsApp sx={{ mr: 1 }} />
                            WhatsApp
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}

                    {/* Conditional Input Fields for Signup */}
                    {deliveryMethod === 'email' ? (
                      <TextField
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                        autoComplete="email"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Mail size={20} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      <TextField
                        label="WhatsApp Number"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                        placeholder="+1234567890"
                        helperText="Include country code (e.g., +1 for US)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone size={20} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    
                    <TextField
                      label="Full Name"
                      name="name"
                      value={trialFormData.name}
                      onChange={handleTrialFormChange}
                      fullWidth
                      margin="normal"
                      required
                    />
                    
                    <Box mt={2} mb={1}>
                      <Typography variant="subtitle2">Account Type</Typography>
                      <RadioGroup
                        row
                        name="userType"
                        value={trialFormData.userType}
                        onChange={handleTrialFormChange}
                      >
                        <FormControlLabel 
                          value="individual" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center">
                              <User style={{ marginRight: 8, width: 16, height: 16 }} /> Individual
                            </Box>
                          } 
                        />
                        <FormControlLabel 
                          value="company" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center">
                              <Building2 style={{ marginRight: 8, width: 16, height: 16 }} /> Company
                            </Box>
                          } 
                        />
                      </RadioGroup>
                    </Box>
                    
                    {trialFormData.userType === 'company' && (
                      <TextField
                        label="Company Name"
                        name="companyName"
                        value={trialFormData.companyName}
                        onChange={handleTrialFormChange}
                        fullWidth
                        margin="normal"
                        required
                      />
                    )}
                    
                    <Alert 
                      severity="info" 
                      sx={{ mt: 2, mb: 3 }}
                    >
                      You'll get full access to core features for 7 days. No credit card required.
                    </Alert>
                    
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Zap />}
                      sx={{ py: 1.5, mt: 2 }}
                    >
                      {loading ? 'Creating Account...' : 'Start Free Trial'}
                    </Button>
                    
                    <Divider sx={{ my: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        Already have an account?
                      </Typography>
                    </Divider>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<LogIn />}
                      sx={{ py: 1.5 }}
                      onClick={() => setShowTrialSignup(false)}
                    >
                      Sign In Instead
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleSendOtp}>
                    {/* Delivery Method Toggle */}
                    {whatsappAvailable && (
                      <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Choose delivery method
                        </Typography>
                        <ToggleButtonGroup
                          value={deliveryMethod}
                          exclusive
                          onChange={(e, newMethod) => {
                            if (newMethod !== null) {
                              setDeliveryMethod(newMethod);
                              setError('');
                              setMessage('');
                            }
                          }}
                          sx={{
                            '& .MuiToggleButton-root': {
                              border: '1px solid',
                              borderColor: '#e0e0e0',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              minWidth: '120px',
                              fontWeight: 500,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                borderColor: '#2563eb',
                                backgroundColor: 'rgba(37, 99, 235, 0.04)'
                              },
                              '&.Mui-selected': {
                                background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
                                color: 'white',
                                borderColor: '#2563eb',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
                                  opacity: 0.95
                                }
                              }
                            }
                          }}
                        >
                          <ToggleButton value="email" aria-label="email delivery">
                            <Mail size={18} style={{ marginRight: '8px' }} />
                            Email
                          </ToggleButton>
                          <ToggleButton value="whatsapp" aria-label="whatsapp delivery">
                            <WhatsApp sx={{ mr: 1 }} />
                            WhatsApp
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}

                    {/* Conditional Input Fields */}
                    {deliveryMethod === 'email' ? (
                      <TextField
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                        autoComplete="email"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Mail size={20} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      <TextField
                        label="WhatsApp Number"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                        placeholder="+1234567890"
                        helperText="Include country code (e.g., +1 for US)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone size={20} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    
                    <Alert 
                      severity="info" 
                      sx={{ mt: 2, mb: 3 }}
                    >
                      We'll send a verification code to your {deliveryMethod === 'email' ? 'email address' : 'WhatsApp number'}.
                    </Alert>
                    
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Mail />}
                      sx={{ py: 1.5 }}
                    >
                      {loading ? 'Sending...' : 'Send Verification Code'}
                    </Button>
                    
                    <Divider sx={{ my: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        New to our Platform?
                      </Typography>
                    </Divider>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<Zap color="#2563eb" />}
                      sx={{ 
                        py: 1.5, 
                        borderColor: '#2563eb',
                        color: '#2563eb',
                        '&:hover': {
                          backgroundColor: 'rgba(37, 99, 235, 0.04)'
                        }
                      }}
                      onClick={() => setShowTrialSignup(true)}
                    >
                      Start 7-Day Free Trial
                    </Button>
                  </form>
                )}
              </CardContent>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoginPage;